const API_BASE = '/api/v1';

interface ApiOptions extends RequestInit {
  json?: unknown;
}

export class ApiError extends Error {
  constructor(public status: number, public problem: unknown, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options;
  const resp = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : (rest.body as BodyInit | null | undefined),
    credentials: 'include',
  });

  if (!resp.ok) {
    let problem: unknown = null;
    try {
      problem = await resp.json();
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, problem, `HTTP ${resp.status}`);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/**
 * Upload a file to /api/v1/attachments. Returns the GUID-based filename the
 * backend stored it as — that exact string goes into a deduction or income
 * form's `attachmentFileName` field on the next PUT.
 */
export async function uploadAttachment(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const resp = await fetch(`${API_BASE}/attachments`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!resp.ok) {
    let problem: unknown = null;
    try {
      problem = await resp.json();
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, problem, `HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as { fileName?: string };
  if (!data.fileName) throw new Error('Attachment upload returned no fileName');
  return data.fileName;
}
